/*
 *
 * Copyright (C) 2022 NetBase Solutions, Inc.
 * 3960 Freedom Circle Suite 201 Santa Clara, CA 95054
 * All rights reserved.
 */

import React from 'react'
// @ts-ignore
import { SimpleBar } from '@netbasequid/chart'
import {
  QuestionMarkOutlined,
  Tooltip,
  CaretDown,
  CaretUp
  // @ts-ignore
} from '@netbasequid/core'
// @ts-ignore
import { sys } from '@netbasequid/theme'

import { Theme } from 'features/ui/types'
import { useDisplayTheme } from 'hooks/useUiState'
import openHelpPage from 'utils/helpLinkUtil'
import LinkButton from 'components/share/LinkButton'
import {
  StyledBarMetricItem,
  StyledBarTable,
  StyledCountInfoBox,
  StyledCountInfoBoxLeft,
  StyledCountInfoBoxRight,
  StyledPercentageText,
  StyledHeader,
  StyledHeaderRow,
  StyledInfoBox,
  StyledIconBox,
  StyledRowBox,
  StyledRowTop,
  StyledRowTopLeft,
  StyledRowTopRight
} from './index.css'
import { BarMetricItemProps } from './type'

const METRIC_TITLE_TESTID = 'metric-title'
const METRIC_VALUE_TESTID = 'metric-total'
const PERCENT_VARIATION_VALUE_TESTID = 'percent-variation-value'
const getDomainNameTestId = (domain: string) => `domain-${domain.toLowerCase()}`

const percentVariationhandler = (
  percentVariation:
    | {
        trend: 'up' | 'down' | 'none'
        value: string
      }
    | undefined,
  theme: Theme
) => {
  switch (percentVariation?.trend) {
    case 'up':
      return (
        <>
          <StyledIconBox theme={theme}>
            <CaretUp color={sys[theme].semantic.positive.default} />
          </StyledIconBox>
          <StyledPercentageText
            theme={theme}
            color={sys[theme].semantic.positive.default}
            data-testid={PERCENT_VARIATION_VALUE_TESTID}>
            {percentVariation.value}
          </StyledPercentageText>
        </>
      )
    case 'down':
      return (
        <>
          <StyledIconBox theme={theme}>
            <CaretDown color={sys[theme].semantic.negative.default} />
          </StyledIconBox>
          <StyledPercentageText
            theme={theme}
            color={sys[theme].semantic.negative.default}
            data-testid={PERCENT_VARIATION_VALUE_TESTID}>
            {percentVariation.value}
          </StyledPercentageText>
        </>
      )
    case 'none':
      return (
        <StyledPercentageText
          theme={theme}
          color={sys[theme].semantic.neutral.default}
          data-testid={PERCENT_VARIATION_VALUE_TESTID}>
          {percentVariation.value}
        </StyledPercentageText>
      )
    default:
      return <></>
  }
}

const BarMetricItem = ({
  hasInfo = true,
  explanation,
  metricTitle,
  total,
  percentVariation,
  data,
  disabledInfo
}: BarMetricItemProps) => {
  const theme = useDisplayTheme() as Theme
  const { link, tooltipContent } = explanation
  return (
    <Tooltip mode={theme} enabled={disabledInfo?.isDisabled} content={disabledInfo?.reason}>
      <StyledBarMetricItem disabled={disabledInfo?.isDisabled}>
        <StyledHeaderRow>
          <StyledHeader theme={theme} data-testid={METRIC_TITLE_TESTID}>
            {metricTitle}
          </StyledHeader>
          {hasInfo && !disabledInfo?.isDisabled && (
            <StyledInfoBox>
              <StyledIconBox theme={theme}>
                <Tooltip mode={theme} enabled={!disabledInfo?.isDisabled} content={tooltipContent}>
                  <LinkButton onClick={() => openHelpPage(link)}>
                    <QuestionMarkOutlined color={sys[theme].text.placeholder} />
                  </LinkButton>
                </Tooltip>
              </StyledIconBox>
            </StyledInfoBox>
          )}
        </StyledHeaderRow>
        <StyledCountInfoBox>
          <StyledCountInfoBoxLeft theme={theme} data-testid={METRIC_VALUE_TESTID}>
            {total}
          </StyledCountInfoBoxLeft>
          <StyledCountInfoBoxRight theme={theme}>
            {percentVariationhandler(percentVariation, theme)}
          </StyledCountInfoBoxRight>
        </StyledCountInfoBox>
        <StyledBarTable>
          {data.map((item) => (
            <StyledRowBox key={item.name} data-testid={getDomainNameTestId(item.name)}>
              <StyledRowTop>
                <StyledRowTopLeft theme={theme}>
                  {item.icon}
                  {item.name}
                </StyledRowTopLeft>
                <StyledRowTopRight theme={theme}>{item.displayValue}</StyledRowTopRight>
              </StyledRowTop>
              <SimpleBar
                adjustLowProgress={false}
                startPoint={item.progressBar.startPoint}
                progress={item.progressBar.progress}
                colorSetting={item.progressBar.colorSetting}
                mode={theme}
                // position of truncation mark is fixed and calculated by design spec: 158/234
                breakSymbolPosition={item.progressBar.hasBreakSymbol ? 67.5 : undefined}
                breakSymbolTooltipContent={
                  item.progressBar.hasBreakSymbol
                    ? item.progressBar.breakSymbolTooltipContent
                    : undefined
                }
              />
            </StyledRowBox>
          ))}
        </StyledBarTable>
      </StyledBarMetricItem>
    </Tooltip>
  )
}

export default BarMetricItem
